import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, 
  Search, 
  Check, 
  Package,
  Loader2
} from 'lucide-react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { getBrands } from '../services/brandService';
import { Brand } from '../types';

interface BrandFilterProps {
  value: string;
  onChange: (brand: string) => void;
  placeholder?: string;
  className?: string;
  zIndex?: number;
}

export function BrandFilter({ value, onChange, placeholder = "Select brand", className = "", zIndex = 1000 }: BrandFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load brands when component mounts or dropdown opens
  useEffect(() => {
    const loadBrands = async () => {
      if (brands.length === 0) {
        try {
          setLoading(true);
          const fetchedBrands = await getBrands();
          setBrands(fetchedBrands);
        } catch (error) {
          console.error('Error loading brands:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    if (isOpen) {
      loadBrands();
    }
  }, [isOpen, brands.length]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelectBrand = (brandName: string) => {
    onChange(brandName);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Create filter options including "All" and existing brands
  const allBrands = [{ id: 'all', name: 'All' }, ...brands];
  
  const filteredBrands = allBrands.filter(brand =>
    brand.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDisplayValue = () => {
    if (value === 'All') return 'All Brands';
    return value || placeholder;
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`} style={{ zIndex }}>
      {/* Selected Value Display / Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-jamaica-green/20 focus:border-jamaica-green transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Package className="w-4 h-4 text-gray-400 shrink-0" />
          {value && value !== 'All' ? (
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
              {value}
            </Badge>
          ) : (
            <span className={`truncate ${value === 'All' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              {getDisplayValue()}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-hidden"
            style={{
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            {/* Search Input */}
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search brands..."
                  className="pl-9 text-sm border-0 focus:ring-0 focus:border-0"
                />
              </div>
            </div>

            {/* Options List */}
            <div className="max-h-48 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                  <span className="ml-2 text-sm text-gray-600">Loading brands...</span>
                </div>
              ) : (
                <>
                  {/* Brands Options */}
                  {filteredBrands.length > 0 ? (
                    filteredBrands.map((brand) => (
                      <button
                        key={brand.id}
                        type="button"
                        onClick={() => handleSelectBrand(brand.name)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center justify-between group"
                      >
                        <span className={`truncate ${brand.name === 'All' ? 'font-medium text-gray-900' : ''}`}>
                          {brand.name === 'All' ? 'All Brands' : brand.name}
                        </span>
                        {value === brand.name && (
                          <Check className="w-4 h-4 text-jamaica-green" />
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center">
                      {searchTerm ? 'No brands found' : 'No brands available'}
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
} 